/**
 * Tâche 1143 (Eric 2026-06-16) — Une famille de notifications système.
 *
 * Mêmes options que « Sentinelle connexion » et « Configuration BYOK » :
 * interrupteur, cases à cocher, « Tester » par personne, retrait d'une adresse
 * ajoutée à la main, adresse libre, et envoi de test immédiat.
 */
import { useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";

export const SystemNotificationFamily = ({
  family,
  busy,
  onSave,
  onVerify,
  onRemove,
  onSendNow,
}) => {
  const [customEmail, setCustomEmail] = useState("");
  const candidates = family.candidates || [];
  const selected = candidates.filter((c) => c.selected);
  const verifiedSelected = selected.filter((c) => c.verified_at);

  const toggle = (email) => {
    const current = selected.map((c) => c.email);
    onSave(family.key, {
      recipients: current.includes(email)
        ? current.filter((e) => e !== email)
        : [...current, email],
    });
  };

  return (
    <div
      className="rounded-xl border border-zinc-200 p-4"
      data-testid={`sysnotif-family-${family.key}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold text-zinc-900">{family.label}</div>
          <p className="text-xs text-zinc-500 mt-0.5">{family.description}</p>
          <p className="text-[11px] text-zinc-400 mt-1">
            Objet type : <span className="font-mono">{family.example_subject}</span>
            {family.scope === "platform" && " · évènement de la plateforme entière"}
          </p>
          <p
            className={`text-xs mt-1.5 font-medium ${
              family.enabled ? "text-emerald-700" : "text-zinc-500"
            }`}
            data-testid={`sysnotif-state-${family.key}`}
          >
            {family.enabled
              ? `Envoi activé · ${
                  family.effective_recipients?.length || 0
                } destinataire(s) : ${
                  (family.effective_recipients || []).join(", ") || "aucun"
                }`
              : "En pause — aucun courriel ne part, même aux adresses cochées"}
          </p>
          {!verifiedSelected.length && (
            <p
              className="text-xs text-amber-700 mt-1"
              data-testid={`sysnotif-verify-required-${family.key}`}
            >
              Activation bloquée : envoyez d'abord « Tester » à au moins un
              destinataire coché.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onSave(family.key, { enabled: !family.enabled })}
          disabled={busy}
          role="switch"
          aria-checked={family.enabled}
          className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            family.enabled ? "bg-[#002FA7]" : "bg-zinc-300"
          }`}
          data-testid={`sysnotif-toggle-${family.key}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              family.enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      <div className="mt-3 space-y-2" data-testid={`sysnotif-recipients-${family.key}`}>
        {family.recipients_are_default && (
          <p className="text-[11px] text-zinc-500 max-w-xl">
            Liste proposée par défaut (fondateurs et administrateurs techniques) :
            rien n'est enregistré tant que vous n'avez pas coché, et rien ne part
            tant que l'envoi est en pause.
          </p>
        )}
        {candidates.map((c) => (
          <div
            key={c.email}
            className="flex items-start justify-between gap-2"
            data-testid={`sysnotif-row-${family.key}-${c.email}`}
          >
            <label className="flex items-start gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={c.selected}
                disabled={busy}
                onChange={() => toggle(c.email)}
                className="mt-1 h-4 w-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]/30"
                data-testid={`sysnotif-checkbox-${family.key}-${c.email}`}
              />
              <span>
                <span className="text-sm font-medium text-zinc-900 group-hover:text-[#002FA7]">
                  {c.name}
                </span>
                {c.is_founder && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#002FA7]/10 text-[#002FA7]">
                    fondateur
                  </span>
                )}
                <span className="block text-xs text-zinc-500">
                  {c.display_role} · {c.email}
                </span>
                {c.verified_at ? (
                  <span
                    className="block text-[11px] text-emerald-700"
                    data-testid={`sysnotif-verified-${family.key}-${c.email}`}
                  >
                    Adresse vérifiée
                  </span>
                ) : (
                  <span
                    className="block text-[11px] text-amber-700"
                    data-testid={`sysnotif-unverified-${family.key}-${c.email}`}
                  >
                    Adresse jamais vérifiée
                  </span>
                )}
              </span>
            </label>
            <div className="shrink-0 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onVerify(family.key, c.email)}
                disabled={busy}
                className="px-2 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                data-testid={`sysnotif-verify-${family.key}-${c.email}`}
              >
                Tester
              </button>
              {c.removable && (
                <button
                  type="button"
                  onClick={() => onRemove(family.key, c.email)}
                  disabled={busy}
                  className="px-2 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-500 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
                  data-testid={`sysnotif-remove-${family.key}-${c.email}`}
                  title={`Retirer définitivement ${c.email} de cette famille`}
                >
                  Retirer
                </button>
              )}
            </div>
          </div>
        ))}
        {!candidates.length && (
          <p className="text-xs text-zinc-500">
            Aucun administrateur ni fondateur dans cette organisation.
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={customEmail}
          onChange={(e) => setCustomEmail(e.target.value)}
          placeholder="autre.adresse@cabinet.com"
          className="flex-1 min-w-[200px] rounded-lg border border-zinc-300 px-3 py-2 text-sm"
          data-testid={`sysnotif-custom-input-${family.key}`}
        />
        <button
          type="button"
          onClick={() => {
            onVerify(family.key, customEmail.trim());
            setCustomEmail("");
          }}
          disabled={busy || !customEmail.trim()}
          className="px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid={`sysnotif-custom-verify-${family.key}`}
        >
          Envoyer un courriel de vérification
        </button>
        <button
          type="button"
          onClick={() => onSendNow(family.key)}
          disabled={busy || !verifiedSelected.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid={`sysnotif-send-now-${family.key}`}
        >
          <PaperPlaneTilt size={16} />
          Envoyer maintenant (test)
        </button>
      </div>
    </div>
  );
};

export default SystemNotificationFamily;
